import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CustomBreadcrumb, CustomInput, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal } from '@/shared/components/molecules';
import { Home, Layers, Trash2, Plus, Pencil } from 'lucide-react';
import CustomModal from '@/shared/components/atoms/CustomModal/CustomModal';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';
import { classApi } from '../api/classApi';
import { ClassItem } from '../types';

const addClassSchema = z.object({
    name: z.string().min(1, 'Class name is required'),
});

type AddClassFormData = z.infer<typeof addClassSchema>;

export const Classes = () => {
    const { user } = useAuthStore();
    const schoolSlug = user?.school?.slug || '';

    const [classes, setClasses] = useState<ClassItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [classToDelete, setClassToDelete] = useState<ClassItem | null>(null);
    const [classToEdit, setClassToEdit] = useState<ClassItem | null>(null);
    const { showToast } = useToastStore();

    const { register, handleSubmit, reset, formState: { errors } } = useForm<AddClassFormData>({
        resolver: zodResolver(addClassSchema),
    });

    const fetchClasses = useCallback(async () => {
        if (!schoolSlug) return;
        setIsLoading(true);
        const response = await classApi.getClasses(schoolSlug, { page, page_size: pageSize });
        if (response.success && response.data) {
            const data = response.data as any;
            if (Array.isArray(data)) {
                setClasses(data);
                setTotalCount(data.length);
                setTotalPages(1);
                setHasNext(false);
                setHasPrevious(false);
            } else {
                setClasses(data.items || []);
                setTotalCount(data.total_count || 0);
                setTotalPages(data.total_pages || 1);
                setHasNext(data.has_next || false);
                setHasPrevious(data.has_previous || false);
            }
        } else {
            showToast(response.error || 'Failed to fetch classes', 'error');
        }
        setIsLoading(false);
    }, [schoolSlug, page, pageSize, showToast]);

    useEffect(() => {
        fetchClasses();
    }, [fetchClasses]);

    const handleDeleteClick = (classItem: ClassItem) => {
        setClassToDelete(classItem);
        setIsDeleteModalOpen(true);
    };

    const handleEditClick = (classItem: ClassItem) => {
        setClassToEdit(classItem);
        reset({ name: classItem.name });
        setIsAddModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (classToDelete && schoolSlug) {
            setIsDeleting(true);
            const response = await classApi.deleteClass(schoolSlug, classToDelete.id);
            if (response.success) {
                setClasses(prev => prev.filter(c => c.id !== classToDelete.id));
                showToast('Class deleted successfully', 'success');
            } else {
                showToast(response.error || 'Failed to delete class', 'error');
            }
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
            setClassToDelete(null);
        }
    };

    const onSubmit = async (data: AddClassFormData) => {
        if (!schoolSlug) return;
        setIsSubmitting(true);

        if (classToEdit) {
            const response = await classApi.updateClass(schoolSlug, classToEdit.id, {
                name: data.name
            });
            if (response.success && response.data) {
                setClasses(prev => prev.map(c => c.id === classToEdit.id ? response.data! : c));
                showToast('Class updated successfully', 'success');
                setIsAddModalOpen(false);
                setClassToEdit(null);
                reset();
            } else {
                showToast(response.error || 'Failed to update class', 'error');
            }
        } else {
            const response = await classApi.createClass(schoolSlug, {
                name: data.name
            });
            if (response.success && response.data) {
                setClasses(prev => [...prev, response.data!]);
                showToast('Class added successfully', 'success');
                setIsAddModalOpen(false);
                reset();
            } else {
                showToast(response.error || 'Failed to add class', 'error');
            }
        }
        setIsSubmitting(false);
    };

    const columns: CustomTableColumn<ClassItem>[] = [
        {
            header: 'Class Name',
            key: 'name',
            render: (classItem) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontWeight: '500' }}>{classItem.name}</span>
                </div>
            )
        },
        {
            header: 'Created At',
            key: 'created_at',
            render: (classItem) => (
                <span>
                    {classItem.created_at
                        ? new Date(classItem.created_at).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                          })
                        : '-'}
                </span>
            )
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            minWidth: '110px',
            width: '110px',
            render: (classItem) => (
                <div className="table-action-cell" style={{ justifyContent: 'flex-end' }}>
                    <button
                        className="table-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleEditClick(classItem); }}
                        title="Edit Class"
                    >
                        <Pencil size={18} />
                    </button>
                    <button
                        className="table-action-btn danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(classItem); }}
                        title="Delete Class"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Classes', icon: <Layers size={14} /> }
                ]}
            />

            <CustomCommonScreenHeader
                title="Class Management"
                subtitle="Manage and organize school classes for student mapping."
                icon={<Layers size={24} />}
                actionLabel="Add Class"
                onActionClick={() => {
                    setClassToEdit(null);
                    reset({ name: '' });
                    setIsAddModalOpen(true);
                }}
            />

            <div style={{ marginTop: '1.25rem' }}>
                <CustomTable
                    columns={columns}
                    data={classes}
                    isLoading={isLoading}
                    isCompact={true}
                    emptyMessage="No classes found"
                    emptyIcon={<Layers size={32} />}
                    pagination={{
                        currentPage: page,
                        totalPages: totalPages,
                        totalCount: totalCount,
                        pageSize: pageSize,
                        onPageChange: setPage,
                        hasPrevious: hasPrevious,
                        hasNext: hasNext,
                        isLoading: isLoading,
                        itemName: 'classes',
                    }}
                />
            </div>

            {/* Add/Edit Class Modal */}
            <CustomModal
                isOpen={isAddModalOpen}
                onClose={() => {
                    setIsAddModalOpen(false);
                    setClassToEdit(null);
                    reset();
                }}
                title={classToEdit ? "Edit Class" : "Add New Class"}
                size="small"
            >
                <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <CustomInput
                        label="Class Name"
                        placeholder="Enter class name (e.g. Class 10)"
                        error={errors.name?.message}
                        {...register('name')}
                        required
                    />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                        <CustomButton
                            type="button"
                            variant="outline"
                            color="secondary"
                            onClick={() => {
                                setIsAddModalOpen(false);
                                setClassToEdit(null);
                                reset();
                            }}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            type="submit"
                            variant="filled"
                            color="primary"
                            loading={isSubmitting}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {classToEdit ? <Pencil size={16} /> : <Plus size={16} />}
                                {classToEdit ? "Save Changes" : "Create Class"}
                            </div>
                        </CustomButton>
                    </div>
                </form>
            </CustomModal>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Class"
                message={`Are you sure you want to delete the class "${classToDelete?.name}"? This action cannot be undone.`}
                confirmLabel={isDeleting ? "Deleting..." : "Delete"}
                variant="danger"
                isLoading={isDeleting}
            />
        </div>
    );
};
